import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Minus, Send, Sparkles } from "lucide-react";

function getSessionId() {
  let sid = localStorage.getItem("ys_chat_session");
  if (!sid) { sid = `chat_${Math.random().toString(36).slice(2)}_${Date.now()}`; localStorage.setItem("ys_chat_session", sid); }
  return sid;
}

let msgSeq = 0;
const withId = (m) => ({ ...m, id: m.id || `m${++msgSeq}` });
const GREETING = withId({ role: "assistant", text: "Hi! I'm Skip 👋 Ask me anything about Yard-skip — plans, the free weekend trial, posting a sale, or finding deals." });

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const sid = useRef(getSessionId());
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (open || location.pathname !== "/browse" || sessionStorage.getItem("ys_skip_tip_seen")) return;
    const t = setTimeout(() => setShowTip(true), 4000);
    return () => clearTimeout(t);
  }, [open, location.pathname]);

  useEffect(() => {
    if (!showTip) return;
    const t = setTimeout(() => dismissTip(), 10000);
    return () => clearTimeout(t);
  }, [showTip]);

  const dismissTip = () => { setShowTip(false); sessionStorage.setItem("ys_skip_tip_seen", "1"); };

  useEffect(() => {
    if (!open) return;
    api.get(`/chat/history/${sid.current}`).then(({ data }) => {
      if (data && data.length) setMessages(data.map(withId));
    }).catch((err) => console.error("Failed to load chat history:", err));
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const sendText = async (text) => {
    const t = (text || "").trim();
    if (!t || loading) return;
    setInput("");
    setMessages((m) => [...m, withId({ role: "user", text: t })]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: t, session_id: sid.current });
      setMessages((m) => [...m, withId({ role: "assistant", text: data.reply })]);
    } catch (err) {
      console.error("Chat request failed:", err);
      setMessages((m) => [...m, withId({ role: "assistant", text: "Sorry, I'm having trouble right now. Please try again in a moment." })]);
    } finally { setLoading(false); }
  };
  const send = () => sendText(input);

  if (!open) {
    return (
      <div className="fixed bottom-5 right-5 z-[1000] flex flex-col items-end gap-3 pointer-events-none">
        {showTip && (
          <div data-testid="chat-proactive-tip" className="pointer-events-none relative w-64 rounded-2xl rounded-br-md bg-white border border-sky-200 shadow-xl shadow-sky-900/15 p-3.5 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <button onClick={dismissTip} data-testid="chat-tip-dismiss" aria-label="Dismiss tip" className="pointer-events-auto absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-3.5 h-3.5" /></button>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-7 h-7 rounded-full bg-ys-sky flex items-center justify-center shrink-0"><Sparkles className="w-3.5 h-3.5 text-white" /></span>
              <p className="font-semibold text-sm text-slate-800">New here?</p>
            </div>
            <p className="text-[13px] text-slate-600 leading-snug">I'm Skip 👋 I can help you find sales near you, pick a plan, or post your own.</p>
            <button onClick={() => { dismissTip(); setOpen(true); sendText("How do I find sales near me?"); }} data-testid="chat-tip-cta"
              className="pointer-events-auto mt-2.5 w-full text-sm font-semibold rounded-full bg-ys-sky text-white py-2 hover:bg-sky-700 transition-colors">Show me how</button>
          </div>
        )}
        <button onClick={() => { dismissTip(); setOpen(true); }} data-testid="chat-open-btn" aria-label="Open help chat"
          className="pointer-events-auto flex items-center gap-2 rounded-full bg-ys-sky text-white shadow-xl shadow-sky-900/20 pl-4 pr-5 h-14 hover:bg-sky-700 transition-transform hover:scale-105">
          <MessageCircle className="w-6 h-6" /> <span className="font-semibold hidden sm:inline">Ask Skip</span>
        </button>
      </div>
    );
  }

  return (
    <div data-testid="chat-panel" className="fixed bottom-5 right-5 z-[1000] w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-2xl shadow-slate-900/25">
      <div className="flex items-center justify-between px-4 py-3 bg-ys-sky text-white shrink-0">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Sparkles className="w-4 h-4" /></span>
          <div><p className="font-semibold text-sm leading-tight">Skip</p><p className="text-[11px] text-sky-100 leading-tight">Yard-skip help</p></div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setOpen(false)} data-testid="chat-minimize-btn" aria-label="Minimize chat" className="p-1.5 rounded-lg hover:bg-white/15"><Minus className="w-4 h-4" /></button>
          <button onClick={() => { setOpen(false); }} data-testid="chat-close-btn" aria-label="Close chat" className="p-1.5 rounded-lg hover:bg-white/15"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-ys-sky text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>{m.text}</div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5"><span className="flex gap-1"><span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} /><span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} /><span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} /></span></div></div>
        )}
      </div>

      {messages.length <= 1 && !loading && (
        <div className="px-3 pb-2 pt-1 flex flex-wrap gap-2 bg-slate-50 shrink-0" data-testid="chat-quick-replies">
          {[["Plans", "What plans and prices do you offer?"], ["Free trial", "How does the free weekend trial work?"], ["Post a sale", "How do I post a sale as a seller?"]].map(([label, q]) => (
            <button key={label} onClick={() => sendText(q)} data-testid={`chat-quick-${label.toLowerCase().replace(/ /g, "-")}`}
              className="text-xs font-semibold rounded-full border border-sky-200 text-ys-sky bg-white px-3 py-1.5 hover:bg-sky-100 transition-colors">{label}</button>
          ))}
        </div>
      )}

      <div className="p-3 border-t border-slate-100 flex items-center gap-2 shrink-0 bg-white">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Ask about plans, trials, posting…" data-testid="chat-input" className="rounded-full h-11" disabled={loading} />
        <Button onClick={send} disabled={loading || !input.trim()} data-testid="chat-send-btn" className="rounded-full bg-ys-sky hover:bg-sky-700 h-11 w-11 p-0 shrink-0"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
